/**
 * AppearanceSync
 *
 * Syncs appearance settings between the elauth API and the elSDK ThemeProvider.
 * On mount: fetches from elauth → applies to ThemeProvider.
 * On change: debounced save back to elauth.
 *
 * Must be rendered inside both ThemeProvider and AuthGuard.
 */

import { useEffect, useRef, useCallback, type ReactNode } from 'react'

import { useTheme } from '@elsapiens/providers'

import { fetchAppearance, saveAppearance, type AppearanceSettings } from '../services/appearance-service'

import type { CustomColors } from '@elsapiens/providers'


// Map between elauth API field names and ThemeProvider values
const VALID_THEMES = ['light', 'dark', 'system'] as const
const VALID_SPACING = ['compact', 'normal', 'spacious'] as const
const VALID_BACKGROUNDS = ['none', 'aurora', 'mesh', 'glow', 'dots', 'gradient-shift', 'northern-lights', 'particles', 'waves', 'blobs', 'network', 'origami', 'snowfall', 'fireflies', 'ripples', 'silk'] as const
const VALID_COLOR_SCHEMES = ['wisteria', 'sapphire', 'emerald', 'amber', 'rose', 'slate', 'violet', 'cyan', 'indigo', 'custom'] as const

function isValid<T extends string>(value: string, list: readonly T[]): value is T {
  return (list as readonly string[]).includes(value)
}

type ThemeSetters = {
  setTheme: (v: typeof VALID_THEMES[number]) => void
  setSpacing: (v: typeof VALID_SPACING[number]) => void
  setBackgroundStyle: (v: typeof VALID_BACKGROUNDS[number]) => void
  setColorScheme: (v: typeof VALID_COLOR_SCHEMES[number]) => void
  setCustomColors: (v: Partial<CustomColors>) => void
}

function applyCoreThemeFields(settings: NonNullable<Awaited<ReturnType<typeof fetchAppearance>>>, setters: ThemeSetters): void {
  if (settings.theme && isValid(settings.theme, VALID_THEMES)) { setters.setTheme(settings.theme) }
  if (settings.spacing_mode && isValid(settings.spacing_mode, VALID_SPACING)) { setters.setSpacing(settings.spacing_mode) }
  if (settings.background_style && isValid(settings.background_style, VALID_BACKGROUNDS)) { setters.setBackgroundStyle(settings.background_style) }
  if (settings.color_scheme && isValid(settings.color_scheme, VALID_COLOR_SCHEMES)) { setters.setColorScheme(settings.color_scheme) }
}

function applyCustomColors(customColors: NonNullable<Awaited<ReturnType<typeof fetchAppearance>>>['custom_colors'], setters: ThemeSetters): void {
  if (!customColors || typeof customColors !== 'object') { return }
  const cc: Partial<CustomColors> = {}
  if (customColors.primary) { cc.primary = customColors.primary }
  if (customColors.background) { cc.background = customColors.background }
  if (customColors.brand_bg) { cc.brandBg = customColors.brand_bg }
  if (customColors.brand_text) { cc.brandText = customColors.brand_text }
  if (Object.keys(cc).length > 0) { setters.setCustomColors(cc) }
}

function applySettings(settings: Awaited<ReturnType<typeof fetchAppearance>>, setters: ThemeSetters): void {
  if (!settings) { return }
  applyCoreThemeFields(settings, setters)
  applyCustomColors(settings.custom_colors, setters)
}

function buildSavePayload(theme: string, colorScheme: string, spacing: string, backgroundStyle: string, customColors: CustomColors): AppearanceSettings {
  return {
    theme: theme,
    color_scheme: colorScheme,
    spacing_mode: spacing,
    background_style: backgroundStyle,
    custom_colors: colorScheme === 'custom'
      ? {
          primary: customColors.primary,
          background: customColors.background,
          ...(customColors.brandBg ? { brand_bg: customColors.brandBg } : {}),
          ...(customColors.brandText ? { brand_text: customColors.brandText } : {}),
        }
      : {},
  }
}

export function AppearanceSync({ children }: { children: ReactNode }): ReactNode {
  const { theme, setTheme, spacing, setSpacing, backgroundStyle, setBackgroundStyle, colorScheme, setColorScheme, customColors, setCustomColors } = useTheme()

  const hasLoadedRef = useRef(false)
  const skipNextSaveRef = useRef(true)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (hasLoadedRef.current) { return }
    hasLoadedRef.current = true
    void (async () => {
      const settings = await fetchAppearance()
      if (!settings) { skipNextSaveRef.current = false; return }
      applySettings(settings, { setTheme, setSpacing, setBackgroundStyle, setColorScheme, setCustomColors })
      requestAnimationFrame(() => { skipNextSaveRef.current = false })
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const doSave = useCallback(() => {
    if (skipNextSaveRef.current) { return }
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current) }
    saveTimerRef.current = setTimeout(() => {
      void saveAppearance(buildSavePayload(theme, colorScheme, spacing, backgroundStyle, customColors))
    }, 1000)
  }, [theme, spacing, backgroundStyle, colorScheme, customColors])

  const initializedRef = useRef(false)
  useEffect(() => {
    if (!initializedRef.current) { initializedRef.current = true; return }
    doSave()
  }, [doSave])

  useEffect(() => { return () => { if (saveTimerRef.current) { clearTimeout(saveTimerRef.current) } } }, [])

  return children
}
